import React, { useState, useEffect, useRef } from 'react';

// Real-time polling enabled
const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activeTab, setActiveTab] = useState('chats');
  const [activeConversation, setActiveConversation] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  const wsRef = useRef(null);
  const activeConversationRef = useRef(activeConversation);

  // Keep ref in sync with state
  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  useEffect(() => {
    fetchData();
    
    // WebSocket for real-time updates
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = protocol + '//' + window.location.host + '/ws';
    
    const connectWS = () => {
      try {
        wsRef.current = new WebSocket(wsUrl);
        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'new_chat' || data.type === 'chat_message') {
              console.log('[Messages] New message via WebSocket, refreshing...');
              fetchData();
            }
          } catch (e) {}
        };
        wsRef.current.onclose = () => { setTimeout(connectWS, 3000); };
      } catch (e) { setTimeout(connectWS, 3000); }
    };
    
    connectWS();
    
    const interval = setInterval(fetchData, 3000);
    return () => { 
      clearInterval(interval); 
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeConversation, conversations]);

  const fetchData = async () => {
    try {
      const chatsRes = await fetch('/api/chats/messages');
      const chatsData = await chatsRes.json();
      
      // Group by sessionId to create conversations
      const grouped = {};
      chatsData.forEach(chat => {
        const sid = chat.sessionId || 'no-session';
        if (!grouped[sid]) {
          grouped[sid] = {
            sessionId: sid,
            name: chat.name,
            email: chat.email,
            messages: [],
            lastMessage: chat.createdAt,
            unread: 0,
            isActive: false
          };
        }
        grouped[sid].messages.push(chat);
        if (!chat.read && !chat.isAdmin) grouped[sid].unread++;
        if (new Date(chat.createdAt) > new Date(grouped[sid].lastMessage)) {
          grouped[sid].lastMessage = chat.createdAt;
        }
      });
      
      // Sort by last message (newest first)
      const convList = Object.values(grouped).sort((a, b) => 
        new Date(b.lastMessage) - new Date(a.lastMessage)
      );
      
      // Preserve active state and update active conversation messages
      // Use ref to get current value even in interval callbacks
      const currentActive = activeConversationRef.current;
      if (currentActive) {
        const updated = convList.find(c => c.sessionId === currentActive.sessionId);
        if (updated) {
          updated.isActive = true;
          // Update the active conversation with new messages
          setActiveConversation(updated);
        }
      }
      
      console.log("[fetchData] Setting conversations:", convList.length); setConversations([...convList]);
      
      const contactsRes = await fetch('/api/contacts');
      const contactsData = await contactsRes.json();
      setContacts(contactsData);
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch:', err);
      setLoading(false);
    }
  };

  const openConversation = (conv) => {
    setActiveConversation({ ...conv, isActive: true });
    setActiveTab('live');
    // Mark as read
    conv.messages.filter(m => !m.read && !m.isAdmin).forEach(m => {
      fetch(`/api/chats/${m.id}/read`, { method: 'PUT' });
    });
  };

  const closeConversation = () => {
    setActiveConversation(null);
    setActiveTab('chats');
  };

  const sendReply = async () => {
    if (!replyMessage.trim() || !activeConversation) return;
    setSending(true);
    try {
      const res = await fetch('/api/chats/reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: activeConversation.sessionId,
          message: replyMessage
        })
      });
      const data = await res.json();
      if (res.ok) {
        setReplyMessage('');
        await fetchData();
        // Re-fetch to get updated messages
        const chatsRes = await fetch('/api/chats/messages');
        const chatsData = await chatsRes.json();
        const sessionMsgs = chatsData.filter(m => m.sessionId === activeConversation.sessionId);
        setActiveConversation({
          ...activeConversation,
          messages: sessionMsgs
        });
      } else {
        alert('Error: ' + (data.error || 'Failed to send'));
      }
    } catch (err) {
      alert('Error sending reply: ' + err.message);
    }
    setSending(false);
  };

  const deleteConversation = async (sessionId) => {
    if (!window.confirm('Delete all messages in this conversation?')) return;
    // Soft-delete the whole session in one call. The backend route
    // /api/chats/:sessionId expects a sessionId (UUID), not a message
    // row id — the previous per-message loop sent numeric ids and
    // silently 404'd, leaving the conversation visible.
    try {
      const res = await fetch(`/api/chats/${sessionId}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        alert('Delete failed: ' + (data.error || `HTTP ${res.status}`));
        return;
      }
    } catch (err) {
      alert('Delete failed: ' + err.message);
      return;
    }
    fetchData();
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      const res = await fetch(`/api/contacts/${id}`, { method: 'DELETE' });
      console.log('[Messages] Delete response:', res.status, res.ok);
      fetchData();
    } catch (err) {
      console.error('[Messages] Delete failed:', err);
      alert('Failed to delete: ' + err.message);
    }
  };

  const unreadTotal = conversations.reduce((sum, c) => sum + c.unread, 0);
  const unreadContacts = contacts.filter(c => !c.read).length;

  if (loading) return <div className="loading">Loading messages...</div>;

  // Reverse message order: oldest first at top, newest at bottom
  const sortedMessages = activeConversation 
    ? [...activeConversation.messages].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    : [];

  // If viewing a conversation in live mode
  const isLiveChat = activeTab === 'live' && activeConversation;

  return (
    <div className="messages-page">
      <div className="page-header">
        <h1>💬 Messages</h1>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'chats' ? 'active' : ''}`}
          onClick={() => { setActiveTab('chats'); setActiveConversation(null); }}
        >
          💬 Chats {unreadTotal > 0 && <span className="tab-badge">{unreadTotal}</span>}
        </button>
        <button 
          className={`tab ${activeTab === 'contacts' ? 'active' : ''}`}
          onClick={() => { setActiveTab('contacts'); setActiveConversation(null); }}
        >
          📝 Contact Form {unreadContacts > 0 && <span className="tab-badge">{unreadContacts}</span>}
        </button>
      </div>

      {/* Chats Tab - Conversation List */}
      {activeTab === 'chats' && (
        <div className="tab-content">
          {conversations.length === 0 ? (
            <div className="empty-state">
              <p>No active conversations.</p>
              <p className="empty-hint">When someone starts a chat from the website, it will appear here.</p>
            </div>
          ) : (
            <div className="conversations-list">
              {conversations.map((conv) => (
                <div 
                  key={conv.sessionId} 
                  className={`conversation-card ${conv.unread > 0 ? 'unread' : ''}`}
                  onClick={() => openConversation(conv)}
                >
                  <div className="conv-avatar">
                    {(conv.name || 'U')[0].toUpperCase()}
                  </div>
                  <div className="conv-info">
                    <div className="conv-header">
                      <strong>{conv.name || 'Unknown'}</strong>
                      <span className="conv-time">{new Date(conv.lastMessage).toLocaleDateString()}</span>
                    </div>
                    <div className="conv-preview">
                      {conv.messages[conv.messages.length - 1]?.message?.substring(0, 60)}...
                    </div>
                    {conv.email && <div className="conv-email">{conv.email}</div>}
                  </div>
                  {conv.unread > 0 && <span className="unread-badge">{conv.unread}</span>}
                  <button 
                    className="btn-delete" 
                    onClick={(e) => { e.stopPropagation(); deleteConversation(conv.sessionId); }}
                  >🗑️</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Live Chat View */}
      {isLiveChat && (
        <div className="live-chat-view">
          <div className="live-chat-header">
            <button className="btn-back" onClick={closeConversation}>← Back</button>
            <div className="chat-with">
              <strong>{activeConversation.name}</strong>
              <span>{activeConversation.email}</span>
            </div>
          </div>
          
          <div className="chat-messages">
            {sortedMessages.map((msg, idx) => (
              <div key={msg.id || idx} className={`chat-message ${msg.isAdmin ? 'admin' : 'user'}`}>
                <div className="msg-bubble">
                  <div className="msg-sender">{msg.isAdmin ? 'You' : msg.name}</div>
                  <div className="msg-text">{msg.message}</div>
                  <div className="msg-time">{new Date(msg.createdAt).toLocaleTimeString()}</div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          
          <div className="chat-input-area">
            <textarea
              className="chat-input"
              placeholder="Type your reply..."
              value={replyMessage}
              onChange={e => setReplyMessage(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
              rows={2}
            />
            <button className="btn btn-primary" onClick={sendReply} disabled={sending || !replyMessage.trim()}>
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      )}

      {/* Contacts Tab */}
      {activeTab === 'contacts' && (
        <div className="tab-content">
          {contacts.length === 0 ? (
            <div className="empty-state">
              <p>No contact form submissions yet.</p>
            </div>
          ) : (
            <div className="contacts-list">
              {contacts.map(contact => (
                <div key={contact.id} className={`contact-card ${contact.read ? '' : 'unread'}`}>
                  <div className="contact-avatar">{(contact.name || 'U')[0].toUpperCase()}</div>
                  <div className="contact-info">
                    <div className="contact-header">
                      <strong>{contact.name}</strong>
                      <span className="contact-date">{new Date(contact.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="contact-email">{contact.email}</div>
                    {contact.phone && <div className="contact-phone">📞 {contact.phone}</div>}
                    {contact.company && <div className="contact-company">🏢 {contact.company}</div>}
                    {contact.service && <div className="contact-service">📋 {contact.service}</div>}
                    <div className="contact-message">{contact.message}</div>
                  </div>
                  <div className="contact-actions">
                    {!contact.read && <button className="btn btn-sm" onClick={() => { fetch(`/api/contacts/${contact.id}/read`, { method: 'PUT' }); fetchData(); }}>Mark Read</button>}
                    <button className="btn btn-sm btn-danger" onClick={() => deleteContact(contact.id)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Messages;
